import { useEffect, useRef, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import type { VectorTileDynamicLayerData, VTTooltipData, VTPopoverData } from '../../Map.types'
import {
  addPillImage,
  setupVTSource,
  setupVTPointLayer,
  setupVTClusterLayers,
  setupVTLabelLayer,
  cleanupVTLayers,
  queryUniqueFeatures,
} from './vectorTileLayerUtils'

interface UseVectorTileDynamicLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  styleLoadedCount: number
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  vectorTileDynamicLayersData?: VectorTileDynamicLayerData[]
  onVectorTileLayerClick?: (properties: any, layerId: string) => void
  onVTTooltipChange?: (data: VTTooltipData | null) => void
  onVTPopoverChange?: (data: VTPopoverData | null) => void
}

const VT_PREFIX = 'vt-dynamic-'

// --- Tooltip HTML builders (fallback for layers without React tooltip) ---

const formatPrice = (price: number | string | null): string => {
  if (!price) return ''
  const numPrice = typeof price === 'string' ? parseFloat(price) : price
  if (isNaN(numPrice)) return ''
  return new Intl.NumberFormat('en-CH', {
    style: 'currency',
    currency: 'CHF',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numPrice)
}

const formatDate = (dateStr: string | null): string | null => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString('fr-CH')
}

const buildListingTooltipHTML = (properties: Record<string, any>): string => {
  const imageUrl = properties?.urls_image || null

  return `
    <div style="min-width: 200px; max-width: 280px; background: #ffffff; border-radius: 8px; overflow: hidden;">
      ${
        imageUrl
          ? `<img src="${imageUrl}" alt="Listing" style="width: 100%; height: 80px; object-fit: cover;" />`
          : `<div style="width: 100%; height: 80px; background: #f5f5f5; display: flex; align-items: center; justify-content: center; color: #999; font-size: 11px;">No image</div>`
      }
      <div style="padding: 10px 12px;">
        <div style="font-size: 16px; font-weight: 700; color: #0294df; margin-bottom: 6px;">
          ${properties?.price ? formatPrice(properties.price) : 'Price on request'}
        </div>
        <div style="font-size: 12px; color: #6b6b6b; line-height: 1.4; margin-bottom: 6px;">
          ${properties?.rooms_nb && properties?.area ? `<div style="margin-bottom: 2px;">${properties.rooms_nb} rooms &bull; ${properties.area} m&sup2;</div>` : ''}
          ${!properties?.rooms_nb && properties?.area ? `<div style="margin-bottom: 2px;">${properties.area} m&sup2;</div>` : ''}
          ${properties?.rooms_nb && !properties?.area ? `<div style="margin-bottom: 2px;">${properties.rooms_nb} rooms</div>` : ''}
          ${properties?.city_name ? `<div>${properties.city_name}</div>` : ''}
          ${properties?.address ? `<div>${properties.address}</div>` : ''}
        </div>
        <div style="display: flex; gap: 4px; flex-wrap: wrap; margin-top: 6px;">
          ${properties?.property_type ? `<span style="display: inline-block; padding: 2px 6px; background: rgba(2, 148, 223, 0.1); color: #0294df; border-radius: 4px; font-size: 10px; font-weight: 500;">${properties.property_type}</span>` : ''}
          ${properties?.deal_type ? `<span style="display: inline-block; padding: 2px 6px; background: rgba(0, 179, 65, 0.1); color: #00b341; border-radius: 4px; font-size: 10px; font-weight: 500;">${properties.deal_type === 'rent' ? 'For Rent' : 'For Sale'}</span>` : ''}
        </div>
      </div>
    </div>
  `
}

const buildTransactionTooltipHTML = (properties: Record<string, any>): string => {
  const reason = properties?.transaction_step_name || 'Transaction'
  const price = properties?.transaction_total_price
  const date = properties?.transaction_date

  return `
    <div style="min-width: 180px; max-width: 260px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
      <div style="background: #10b981; padding: 8px 12px;">
        <div style="font-size: 11px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px;">
          ${reason}
        </div>
      </div>
      <div style="padding: 12px;">
        ${
          price
            ? `<div style="font-size: 16px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px;">${formatPrice(price)}</div>`
            : ''
        }
        <div style="font-size: 11px; color: #6b7280; line-height: 1.5;">
          ${date ? `<div style="margin-bottom: 4px;">${formatDate(date)}</div>` : ''}
        </div>
      </div>
    </div>
  `
}

const buildBuildingPermitTooltipHTML = (properties: Record<string, any>): string => {
  const statusLabels: Record<string, string> = {
    approved: 'Approved',
    pending: 'Pending',
    rejected: 'Rejected',
    completed: 'Completed',
    inquiry: 'In Inquiry',
  }
  const statusColors: Record<string, string> = {
    approved: '#22c55e',
    pending: '#f59e0b',
    rejected: '#ef4444',
    completed: '#3b82f6',
    inquiry: '#8b5cf6',
  }

  const status = (properties?.latest_status_db || 'pending').toLowerCase()
  const statusLabel = statusLabels[status] || properties?.latest_status_db || 'Unknown'
  const statusColor = statusColors[status] || '#8b5cf6'

  return `
    <div style="min-width: 200px; max-width: 280px; background: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
      <div style="background: #8b5cf6; padding: 8px 12px;">
        <div style="font-size: 11px; font-weight: 600; color: #ffffff; text-transform: uppercase; letter-spacing: 0.5px;">
          Building Permit
        </div>
      </div>
      <div style="padding: 12px;">
        <div style="font-size: 13px; font-weight: 600; color: #1a1a1a; margin-bottom: 8px; line-height: 1.4;">
          ${properties?.description || 'No description'}
        </div>
        <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px;">
          <span style="display: inline-block; padding: 2px 8px; background: ${statusColor}20; color: ${statusColor}; border-radius: 4px; font-size: 10px; font-weight: 600;">
            ${statusLabel}
          </span>
          ${
            properties?.work_type_official
              ? `<span style="display: inline-block; padding: 2px 8px; background: #f3f4f6; color: #6b7280; border-radius: 4px; font-size: 10px; font-weight: 500;">${properties.work_type_official}</span>`
              : ''
          }
        </div>
        <div style="font-size: 11px; color: #6b7280; line-height: 1.5;">
          ${properties?.city_name ? `<div style="margin-bottom: 2px;">${properties.city_name}${properties.postal_code ? ` (${properties.postal_code})` : ''}</div>` : ''}
          ${properties?.inquiry_start_date ? `<div>${formatDate(properties.inquiry_start_date)}</div>` : ''}
        </div>
      </div>
    </div>
  `
}

// --- Hook ---

export const useVectorTileDynamicLayers = ({
  map,
  mapLoaded,
  styleLoadedCount,
  popup,
  vectorTileDynamicLayersData,
  onVectorTileLayerClick,
  onVTTooltipChange,
  onVTPopoverChange,
}: UseVectorTileDynamicLayersProps) => {
  // Store callback refs to avoid recreating layers
  const onClickRef = useRef(onVectorTileLayerClick)
  const onTooltipRef = useRef(onVTTooltipChange)
  const onPopoverRef = useRef(onVTPopoverChange)

  useEffect(() => {
    onClickRef.current = onVectorTileLayerClick
    onTooltipRef.current = onVTTooltipChange
    onPopoverRef.current = onVTPopoverChange
  }, [onVectorTileLayerClick, onVTTooltipChange, onVTPopoverChange])

  // Track feature state for cleanup
  const hoveredStateRef = useRef<
    Map<string, { source: string; sourceLayer: string; id: string | number }>
  >(new Map())
  const selectedStateRef = useRef<
    Map<string, { source: string; sourceLayer: string; id: string | number }>
  >(new Map())

  // Track registered event handlers for cleanup
  const registeredHandlersRef = useRef<
    Map<string, Array<{ event: string; layer?: string; handler: any }>>
  >(new Map())

  /**
   * Remove all event handlers for a given layer group.
   */
  const cleanupHandlers = useCallback(
    (layerGroupId: string) => {
      const handlers = registeredHandlersRef.current.get(layerGroupId)
      if (!handlers || !map.current) return

      handlers.forEach(({ event, layer, handler }) => {
        try {
          if (layer) {
            map.current?.off(event, layer, handler)
          } else {
            map.current?.off(event, handler)
          }
        } catch {
          // ignore
        }
      })
      registeredHandlersRef.current.delete(layerGroupId)
    },
    [map]
  )

  /**
   * Register an event handler and track it for cleanup.
   */
  const registerHandler = useCallback(
    (
      layerGroupId: string,
      event: string,
      layerOrHandler: string | ((...args: any[]) => void),
      handler?: (...args: any[]) => void
    ) => {
      if (!map.current) return

      if (!registeredHandlersRef.current.has(layerGroupId)) {
        registeredHandlersRef.current.set(layerGroupId, [])
      }
      const handlers = registeredHandlersRef.current.get(layerGroupId)
      if (!handlers) return

      if (typeof layerOrHandler === 'string' && handler) {
        map.current.on(event, layerOrHandler, handler)
        handlers.push({ event, layer: layerOrHandler, handler })
      } else if (typeof layerOrHandler === 'function') {
        map.current.on(event, layerOrHandler)
        handlers.push({ event, handler: layerOrHandler })
      }
    },
    [map]
  )

  // Main effect: manage layers
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // Get all currently existing vector tile dynamic layers/sources
    let existingLayers: string[] = []
    let existingSources: string[] = []

    try {
      const style = map.current.getStyle()
      if (style?.layers) {
        existingLayers = style.layers
          .filter((layer) => layer.id.startsWith(VT_PREFIX))
          .map((layer) => layer.id)
      }
      if (style?.sources) {
        existingSources = Object.keys(style.sources).filter((source) =>
          source.startsWith(VT_PREFIX)
        )
      }
    } catch (error) {
      console.warn('Failed to get existing vt-dynamic layers/sources:', error)
      return
    }

    // If no data, remove all existing vt-dynamic layers/sources
    if (!vectorTileDynamicLayersData || vectorTileDynamicLayersData.length === 0) {
      // Cleanup all handlers
      registeredHandlersRef.current.forEach((_handlers, groupId) => {
        cleanupHandlers(groupId)
      })

      existingLayers.forEach((layerId) => {
        try {
          if (map.current?.getLayer(layerId)) {
            map.current.removeLayer(layerId)
          }
        } catch (e) {
          console.warn(`Failed to remove vt layer ${layerId}:`, e)
        }
      })
      existingSources.forEach((sourceId) => {
        try {
          if (map.current?.getSource(sourceId)) {
            map.current.removeSource(sourceId)
          }
        } catch (e) {
          console.warn(`Failed to remove vt source ${sourceId}:`, e)
        }
      })
      return
    }

    const currentLayerIds = new Set<string>()
    const currentSourceIds = new Set<string>()

    vectorTileDynamicLayersData.forEach((layerData) => {
      const {
        id,
        tileUrl,
        sourceLayer,
        visible,
        pointStyle,
        clustering,
        labelStyle,
        tooltip,
        overlapPopover,
      } = layerData
      const sourceId = `${VT_PREFIX}${id}-source`
      const circleLayerId = `${VT_PREFIX}${id}-circle`
      const clusterLayerId = `${circleLayerId}-cluster`
      const clusterCountLayerId = `${circleLayerId}-cluster-count`
      const labelLayerId = `${circleLayerId}-label`
      const pillImgId = `${circleLayerId}-pill-bg`

      const color = pointStyle?.color || '#0294df'
      const size = pointStyle?.size || 5
      const strokeColor = pointStyle?.strokeColor || '#ffffff'
      const strokeWidth = pointStyle?.strokeWidth || 1
      const opacity = pointStyle?.opacity ?? 0.7

      const clusterEnabled = clustering?.enabled ?? false
      const clusterMaxZoom = clustering?.maxZoom ?? 13

      const hasReactTooltip = tooltip?.enabled && (tooltip.render || onTooltipRef.current)
      const hasReactPopover = overlapPopover?.enabled && onPopoverRef.current

      try {
        const existingSource = map.current?.getSource(sourceId) as any

        // Vector tile sources can't have their URL updated
        if (existingSource) {
          const existingTiles = existingSource?._options?.tiles?.[0]
          if (existingTiles !== tileUrl) {
            // URL changed — clean up everything
            cleanupHandlers(id)
            cleanupVTLayers(
              map.current,
              [labelLayerId, clusterCountLayerId, clusterLayerId, circleLayerId],
              [sourceId],
              [pillImgId]
            )
          }
        }

        const mapInstance = map.current
        if (!mapInstance) return

        // Add source if not present
        if (!mapInstance.getSource(sourceId)) {
          setupVTSource(mapInstance, sourceId, tileUrl, clusterEnabled ? 24 : 14)
        }

        // Add circle layer if not present
        if (!mapInstance.getLayer(circleLayerId)) {
          // Clean up old handlers for this group
          cleanupHandlers(id)

          // Insert before the first dynamic- layer so GeoJSON markers render on top
          let beforeLayerId: string | undefined
          try {
            const style = mapInstance.getStyle()
            const dynamicLayer = style?.layers?.find((l) => l.id.startsWith('dynamic-'))
            if (dynamicLayer) {
              beforeLayerId = dynamicLayer.id
            }
          } catch {
            // ignore
          }

          // Use shared util for point layer
          setupVTPointLayer(
            mapInstance,
            circleLayerId,
            sourceId,
            sourceLayer,
            {
              color,
              size,
              strokeColor,
              strokeWidth,
              clusterEnabled,
              clusterMaxZoom,
            },
            beforeLayerId
          )

          // Override circle-opacity if no clustering
          if (!clusterEnabled) {
            mapInstance.setPaintProperty(circleLayerId, 'circle-opacity', opacity)
          }

          // Add cluster layers
          if (clusterEnabled) {
            setupVTClusterLayers(mapInstance, circleLayerId, sourceId, {
              aggsSourceLayer: clustering?.aggsSourceLayer || 'aggs',
              maxZoom: clusterMaxZoom,
              clusterColor: clustering?.clusterColor || color,
              minRadius: clustering?.minRadius ?? 14,
              maxRadius: clustering?.maxRadius ?? 40,
            })

            // Cluster click handler - zoom in
            registerHandler(id, 'mouseenter', clusterLayerId, () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = 'pointer'
              }
            })
            registerHandler(id, 'mouseleave', clusterLayerId, () => {
              if (map.current) {
                map.current.getCanvas().style.cursor = ''
              }
            })
            registerHandler(id, 'click', clusterLayerId, (e: any) => {
              if (onPopoverRef.current) {
                onPopoverRef.current(null)
              }
              const targetZoom = Math.min((map.current?.getZoom() || 0) + 2, 14)
              map.current?.flyTo({
                center: e.lngLat,
                zoom: targetZoom,
              })
            })
          }

          // Add label layer
          if (labelStyle?.enabled) {
            const {
              field = 'price',
              backgroundColor = '#ffffff',
              textColor = '#000000',
              fontSize = 12,
              minZoom = 10,
              pill = false,
              textFieldExpression,
            } = labelStyle

            if (pill) {
              addPillImage(mapInstance, pillImgId, backgroundColor)
            }

            setupVTLabelLayer(
              mapInstance,
              labelLayerId,
              sourceId,
              sourceLayer,
              {
                field,
                backgroundColor,
                textColor,
                fontSize,
                minZoom,
                pill,
                textFieldExpression,
              },
              pill ? pillImgId : undefined
            )
          }

          // --- Event handlers ---

          // Click handler. Bound to both the geo-center circle and the
          // wider label pill so clicks anywhere on the visible pill
          // register, not just the exact center anchor.
          //
          // Use `e.features` from the click event itself (Mapbox already
          // matched them for the bound layer) rather than re-querying via
          // `queryUniqueFeatures` — that helper dedupes by feature id and
          // silently drops features whose tiles don't carry an id, which
          // would swallow clicks on the label pill.
          const handleLayerClick = (e: any) => {
            if (onTooltipRef.current) onTooltipRef.current(null)

            const eventFeatures = (e.features || []) as any[]

            // Look across both layers at the click point so we can detect
            // overlap (multiple distinct listings stacked) and trigger the
            // popover. Falls back to e.features if the query returns nothing.
            const overlapFeatures = queryUniqueFeatures(map.current, e.point, [
              circleLayerId,
              labelLayerId,
            ])
            const features = overlapFeatures.length > 0 ? overlapFeatures : eventFeatures

            if (features.length === 0) return

            if (features.length > 1 && hasReactPopover && onPopoverRef.current) {
              const featuresWithId = features.map((f: any) => {
                if (f.id != null && f.properties?.id == null) {
                  return { ...f, properties: { ...f.properties, id: f.id } }
                }
                return f
              })
              onPopoverRef.current({
                features: featuresWithId,
                x: e.point.x,
                y: e.point.y,
              })
            } else {
              if (onPopoverRef.current) onPopoverRef.current(null)
              if (onClickRef.current) {
                const feature = features[0]
                const props = { ...feature.properties }
                if (feature.id != null && props.id == null) {
                  props.id = feature.id
                }
                onClickRef.current(props, id)
              }
            }
          }

          registerHandler(id, 'click', circleLayerId, handleLayerClick)

          // Dismiss popover on empty click or movestart. Also include
          // the label layer here so clicking the visible pill keeps the
          // popover open (treated as "non-empty").
          registerHandler(id, 'click', (e: any) => {
            const candidateLayers = [circleLayerId, labelLayerId].filter(
              (lid) => !!map.current?.getLayer(lid)
            )
            const features = candidateLayers.length
              ? map.current?.queryRenderedFeatures(e.point, {
                  layers: candidateLayers,
                })
              : []
            if (!features || features.length === 0) {
              if (onPopoverRef.current) onPopoverRef.current(null)
            }
          })
          registerHandler(id, 'movestart', () => {
            if (onPopoverRef.current) onPopoverRef.current(null)
          })

          // Hover handler
          registerHandler(id, 'mouseenter', circleLayerId, (e: any) => {
            if (!e.features?.[0] || !map.current) return
            const feature = e.features[0]

            // Set hover feature state
            if (feature.id !== undefined && feature.id !== null) {
              map.current.setFeatureState(
                { source: sourceId, sourceLayer, id: feature.id },
                { hover: true }
              )
              hoveredStateRef.current.set(id, {
                source: sourceId,
                sourceLayer,
                id: feature.id,
              })
            }

            // Tooltip
            if (hasReactTooltip) {
              const uniqueFeatures = queryUniqueFeatures(map.current, e.point, [circleLayerId])
              onTooltipRef.current({
                feature,
                x: e.point.x,
                y: e.point.y,
                overlapCount: uniqueFeatures.length > 1 ? uniqueFeatures.length - 1 : undefined,
              })
            } else if (tooltip?.enabled) {
              // Fallback to Mapbox Popup HTML
              const properties = feature.properties || {}
              const geometry = feature.geometry as any
              const coordinates = geometry?.coordinates?.slice?.()
              if (coordinates && popup.current && map.current) {
                while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                  coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360
                }

                let tooltipHTML = ''
                if (id.includes('purchase_listings') || id.includes('rental_listings')) {
                  tooltipHTML = buildListingTooltipHTML(properties)
                } else if (id.includes('transactions')) {
                  tooltipHTML = buildTransactionTooltipHTML(properties)
                } else if (id.includes('building_permits')) {
                  tooltipHTML = buildBuildingPermitTooltipHTML(properties)
                }

                if (tooltipHTML) {
                  popup.current.setLngLat(coordinates).setHTML(tooltipHTML).addTo(map.current)
                }
              }
            }

            map.current.getCanvas().style.cursor = 'pointer'
          })

          registerHandler(id, 'mouseleave', circleLayerId, (e: any) => {
            if (!map.current) return

            // Clear hover feature state
            const hoveredState = hoveredStateRef.current.get(id)
            if (hoveredState) {
              try {
                map.current.setFeatureState(hoveredState, { hover: false })
              } catch {
                // ignore
              }
              hoveredStateRef.current.delete(id)
            }

            // Don't clear tooltip if mouse moved to label layer
            if (labelStyle?.enabled && e.point && map.current.getLayer(labelLayerId)) {
              const labelFeatures = map.current.queryRenderedFeatures(e.point, {
                layers: [labelLayerId],
              })
              if (labelFeatures.length > 0) return
            }

            if (onTooltipRef.current) onTooltipRef.current(null)
            if (popup.current) popup.current.remove()
            map.current.getCanvas().style.cursor = ''
          })

          // Label layer event handlers
          if (labelStyle?.enabled) {
            const labelFontSize = labelStyle.fontSize || 12

            registerHandler(id, 'mouseenter', labelLayerId, (e: any) => {
              if (!e.features?.[0] || !map.current) return
              const feature = e.features[0]

              if (hasReactTooltip) {
                const labelOffset = labelFontSize * 1.5 + 10
                const uniqueFeatures = queryUniqueFeatures(map.current, e.point, [circleLayerId])
                onTooltipRef.current({
                  feature,
                  x: e.point.x,
                  y: e.point.y + labelOffset,
                  overlapCount: uniqueFeatures.length > 1 ? uniqueFeatures.length - 1 : undefined,
                })
              }
              map.current.getCanvas().style.cursor = 'pointer'
            })

            registerHandler(id, 'mouseleave', labelLayerId, (e: any) => {
              if (!map.current) return
              if (e.point && map.current.getLayer(circleLayerId)) {
                const circleFeatures = map.current.queryRenderedFeatures(e.point, {
                  layers: [circleLayerId],
                })
                if (circleFeatures.length > 0) return
              }
              if (onTooltipRef.current) onTooltipRef.current(null)
              map.current.getCanvas().style.cursor = ''
            })

            registerHandler(id, 'click', labelLayerId, handleLayerClick)
          }
        }

        // Update visibility for all layers in this group
        const allLayerIds = [circleLayerId, clusterLayerId, clusterCountLayerId, labelLayerId]
        allLayerIds.forEach((lid) => {
          if (map.current?.getLayer(lid)) {
            map.current.setLayoutProperty(lid, 'visibility', visible ? 'visible' : 'none')
          }
        })

        // Track current layers/sources
        currentSourceIds.add(sourceId)
        currentLayerIds.add(circleLayerId)
        if (clusterEnabled) {
          currentLayerIds.add(clusterLayerId)
          currentLayerIds.add(clusterCountLayerId)
        }
        if (labelStyle?.enabled) {
          currentLayerIds.add(labelLayerId)
        }
      } catch (error) {
        console.warn(`Failed to add/update vt-dynamic layer ${id}:`, error)
      }
    })

    // Remove layers/sources that are no longer in data
    existingLayers.forEach((layerId) => {
      if (!currentLayerIds.has(layerId)) {
        try {
          if (map.current?.getLayer(layerId)) {
            map.current.removeLayer(layerId)
          }
        } catch (e) {
          console.warn(`Failed to remove vt layer ${layerId}:`, e)
        }
      }
    })
    existingSources.forEach((sourceId) => {
      if (!currentSourceIds.has(sourceId)) {
        try {
          if (map.current?.getSource(sourceId)) {
            map.current.removeSource(sourceId)
          }
        } catch (e) {
          console.warn(`Failed to remove vt source ${sourceId}:`, e)
        }
      }
    })
  }, [
    vectorTileDynamicLayersData,
    mapLoaded,
    styleLoadedCount,
    cleanupHandlers,
    registerHandler,
    map,
    popup,
  ])

  // Separate effect: hover/select feature-state sync from props
  useEffect(() => {
    if (!map.current || !mapLoaded || !vectorTileDynamicLayersData) return

    vectorTileDynamicLayersData.forEach((layerData) => {
      const { id, sourceLayer, hoveredItemId, selectedItemId } = layerData
      const sourceId = `${VT_PREFIX}${id}-source`
      const circleLayerId = `${VT_PREFIX}${id}-circle`

      if (!map.current?.getLayer(circleLayerId)) return

      // Manage selected feature state
      const prevSelected = selectedStateRef.current.get(id)
      if (prevSelected) {
        try {
          map.current.setFeatureState(prevSelected, { selected: false })
        } catch {
          // ignore
        }
        selectedStateRef.current.delete(id)
      }

      if (selectedItemId != null) {
        // Find the feature on the map by scanning rendered features
        try {
          const renderedFeatures = map.current.queryRenderedFeatures(undefined, {
            layers: [circleLayerId],
          })
          const match = renderedFeatures.find(
            (f: any) => (f.properties?.id ?? f.id) == selectedItemId
          )
          if (match && match.id != null) {
            const state = { source: sourceId, sourceLayer, id: match.id }
            map.current.setFeatureState(state, { selected: true })
            selectedStateRef.current.set(id, state)
          }
        } catch {
          // ignore - features may not be rendered at current viewport
        }
      }

      // Manage hovered feature state from props (bidirectional sync from list hover)
      const prevHovered = hoveredStateRef.current.get(`prop-${id}`)
      if (prevHovered) {
        try {
          map.current.setFeatureState(prevHovered, { hover: false })
        } catch {
          // ignore
        }
        hoveredStateRef.current.delete(`prop-${id}`)
      }

      if (hoveredItemId != null) {
        try {
          const renderedFeatures = map.current.queryRenderedFeatures(undefined, {
            layers: [circleLayerId],
          })
          const match = renderedFeatures.find(
            (f: any) => (f.properties?.id ?? f.id) == hoveredItemId
          )
          if (match && match.id != null) {
            const state = { source: sourceId, sourceLayer, id: match.id }
            map.current.setFeatureState(state, { hover: true })
            hoveredStateRef.current.set(`prop-${id}`, state)
          }
        } catch {
          // ignore
        }
      }
    })
  }, [vectorTileDynamicLayersData, mapLoaded, map])
}
